import { meetingService, type Meeting } from "@/api/calendar";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { Textarea } from "../ui/textarea";
import { PaginatedSelect } from "../paginated-select";
import { get_cases } from "@/api/case";
import { TimePicker } from "../time-picker";
import { get_users } from "@/api/user";
import { Case, Person } from "@/views/cases/data/schema";
import { handleServerError } from "@/utils/handle-server-error";

interface AppointmentFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedDate: Date;
  onSubmit: (meeting?: Meeting) => void;
  editMode?: boolean;
  postponeMode?: boolean;
  meetingToEdit?: Meeting;
  onUpdate: () => void;
}

interface CasesFetch {
  items: Case[],
  hasMore: boolean
}

export function AppointmentFormModal({
  open,
  onOpenChange,
  selectedDate,
  onSubmit,
  onUpdate,
  editMode = false,
  postponeMode = false,
  meetingToEdit,
}: AppointmentFormModalProps) {
  const { t } = useTranslation();
  const [assigned, setAssigned] = useState<Person | null>(meetingToEdit?.assigned_to || null);
  const [cases, setCases] = useState<CasesFetch | null>(null);
  const [postponeDate, setPostponeDate] = useState<Date | undefined>(
    meetingToEdit?.start_time ? new Date(meetingToEdit.start_time * 1000) : new Date(selectedDate)
  );

  const formSchema = z
    .object({
      desc: z.string().min(1, { message: t('descriptionRequired') }),
      case: z.number().nullish(),
      meetingType: z.enum(["court", "office"]),
      assigned_to: z.string().nullish().refine(v => v && v.length > 0, { message: t("assigned_to_needed") }),
      start_time: z.number().nullish().refine(v => v && v > 0, { message: t("startTimeRequired") }),
      end_time: z.number().nullish()
    })
    .superRefine(
      (data, ctx) => {
        if (data.meetingType === "court" && (!data.case || data.case <= 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("caseRequired"),
            path: ["case"]
          })
        }

        if (data.start_time && data.end_time && data.start_time >= data.end_time) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("endTimeAfterStart"),
            path: ["end_time"]
          })
        }
      }
    );

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      desc: "",
      case: undefined,
      meetingType: "court",
      assigned_to: "",
      start_time: undefined,
      end_time: undefined,
    },
  });

  const meetingType = useWatch({ control: form.control, name: "meetingType" });
  const startTime = useWatch({ control: form.control, name: "start_time" });

  // Form times are unix seconds; the picker works in hour/minute on the selected day.
  const toHM = (ts?: number | null) => {
    if (!ts) return undefined;
    const d = new Date(ts * 1000);
    return { hour: d.getHours(), minute: d.getMinutes() };
  };
  const toTimestamp = ({ hour, minute }: { hour: number; minute: number }) => {
    const d = new Date(postponeMode && postponeDate ? postponeDate : selectedDate);
    d.setHours(hour, minute, 0, 0);
    return Math.floor(d.getTime() / 1000);
  };
  const startHM = toHM(startTime);

  const fetchCases = async (page: number, search?: string): Promise<CasesFetch> => {
    const list = await get_cases({
      ordering: null,
      page: (page - 1) * 30,
      page_size: 30,
      name: search ? search : null
    })
    return {
      items: list.results.map(caseItem => ({
        ...caseItem,
        name: `${caseItem.name} (${caseItem.client.name})`
      })),
      hasMore: list.next != null,
    }
  }

  const fetchUsers = async (page: number, search?: string) => {
    const list = await get_users({
      roles: null,
      is_active: null,
      ordering: null,
      page: (page - 1) * 30,
      page_size: 30,
      name: search ? search : null
    })
    return {
      items: list.results,
      hasMore: list.next != null,
    }
  }

  useEffect(() => {
    if (open) {
      if ((editMode || postponeMode) && meetingToEdit) {
        // In edit or postpone mode, populate the form with the meeting data
        const meetingType = meetingToEdit.tribunal_proc_type === null || meetingToEdit.tribunal_proc_type === false
          ? "office"
          : "court";

        // If in postpone mode, update the postpone date
        if (postponeMode && meetingToEdit.start_time) {
          setPostponeDate(new Date(meetingToEdit.start_time * 1000));
        }

        form.reset({
          desc: meetingToEdit.desc || "",
          case: meetingToEdit.case?.id || undefined,
          meetingType: meetingType as "court" | "office",
          assigned_to: meetingToEdit.assigned_to.id || "",
          start_time: meetingToEdit.start_time || undefined,
          end_time: meetingToEdit.end_time || undefined,
        });
      } else if (selectedDate) {
        form.reset({
          desc: "",
          case: undefined,
          meetingType: "office",
          assigned_to: undefined,
          start_time: undefined,
          end_time: undefined,
        });
      }
    }
    // We specifically only want to re-run this when open, selectedDate, editMode, postponeMode, or meetingToEdit change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDate, editMode, postponeMode, meetingToEdit]);

  const handleSubmitForm = (data: FormValues) => {
    const { meetingType, ...submissionData } = data;

    // Create a deep copy of the submission data
    const currentPayload: any = {
      ...submissionData,
      assigned_to: submissionData.assigned_to || "",
      tribunal_proc_type: meetingType === "court",
      start_time: submissionData.start_time || 0,
      end_time: submissionData.end_time || null
    };

    // Only include case field if it has a valid value
    if (submissionData.case && submissionData.case > 0) {
      currentPayload.case = submissionData.case;
    }

    // In postpone mode, ensure the date uses the selected postpone date
    if (postponeMode && postponeDate) {
      // Keep the time but update the date for both start and end times
      if (currentPayload.start_time) {
        const startDateTime = new Date(currentPayload.start_time * 1000);
        const newStartDate = new Date(postponeDate);
        newStartDate.setHours(
          startDateTime.getHours(),
          startDateTime.getMinutes(),
          startDateTime.getSeconds()
        );
        currentPayload.start_time = Math.floor(newStartDate.getTime() / 1000);
      }

      if (currentPayload.end_time) {
        const endDateTime = new Date(currentPayload.end_time * 1000);
        const newEndDate = new Date(postponeDate);
        newEndDate.setHours(
          endDateTime.getHours(),
          endDateTime.getMinutes(),
          endDateTime.getSeconds()
        );
        currentPayload.end_time = Math.floor(newEndDate.getTime() / 1000);
      }
    }

    if ((editMode || postponeMode) && meetingToEdit) {
      const originalPayloadEquivalent = {
        desc: meetingToEdit.desc || "",
        case: meetingToEdit.case?.id,
        assigned_to: meetingToEdit.assigned_to.id,
        tribunal_proc_type: meetingToEdit.tribunal_proc_type,
        start_time: meetingToEdit.start_time,
        end_time: meetingToEdit.end_time,
      };

      let noChanges = true;
      if (
        originalPayloadEquivalent.desc !== currentPayload.desc ||
        originalPayloadEquivalent.case !== currentPayload.case ||
        originalPayloadEquivalent.tribunal_proc_type !== currentPayload.tribunal_proc_type ||
        originalPayloadEquivalent.assigned_to !== currentPayload.assigned_to ||
        originalPayloadEquivalent.start_time !== currentPayload.start_time ||
        originalPayloadEquivalent.end_time !== currentPayload.end_time
      ) {
        noChanges = false;
      }

      // In postpone mode, we always want to submit even if no changes were made
      if (noChanges && !postponeMode) {
        onOpenChange(false);
        return;
      }
    }

    const future = postponeMode && meetingToEdit
      ? meetingService.postponeMeeting(meetingToEdit?.id, currentPayload)
      : editMode && meetingToEdit
        ? meetingService.updateMeeting(meetingToEdit?.id, currentPayload)
        : meetingService.createMeeting(currentPayload)

    future
      .then((returnedMeeting) => {
        form.reset()
        toast({
          title: t(postponeMode ? 'appointmentPostponed' : editMode ? 'appointmentUpdated' : 'appointmentCreated'),
        })
        onOpenChange(false)
        // When postponing, we pass both the original meeting ID and the new meeting
        // so the parent component can update both meetings
        if (postponeMode && meetingToEdit) {
          onSubmit({
            ...returnedMeeting,
            _originalMeetingId: meetingToEdit.id // Add a temporary field to track the original meeting
          });
        } else {
          onSubmit(returnedMeeting);
        }
        if (editMode || postponeMode) {
          onUpdate()
        }
      })
      .catch(e => {
        handleServerError(e)
      });
  };

  const handleModalOpenChange = (open: boolean) => {
    if (!open) {
      setAssigned(null)
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleModalOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {postponeMode ? t("postponeAppointment") : editMode ? t("editAppointment") : t("createNewAppointment")}
          </DialogTitle>
          <DialogDescription>
            {`${postponeMode ? t("postponeAppointmentFor") : editMode ? t("editAppointmentFor") : t("createAppointmentFor")
              } ${format(postponeMode && postponeDate ? postponeDate : selectedDate, "EEEE, d MMMM yyyy", {
                locale: ar,
              })}`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmitForm)}
            className="space-y-4"
          >
            {postponeMode && (
              <FormItem className='grid grid-cols-1 sm:grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                <FormLabel className='sm:col-span-2 text-right'>
                  {t("postponeDate")}
                </FormLabel>
                <div className="sm:col-span-4">
                  <DatePicker
                    date={postponeDate}
                    setDate={(date) => {
                      if (date) {
                        setPostponeDate(date);

                        // When the date changes, update the start and end times to use the new date
                        const startTimeValue = form.getValues("start_time");
                        if (startTimeValue) {
                          const startDateTime = new Date(startTimeValue * 1000);
                          const newStartDate = new Date(date);
                          newStartDate.setHours(
                            startDateTime.getHours(),
                            startDateTime.getMinutes(),
                            startDateTime.getSeconds()
                          );
                          form.setValue("start_time", newStartDate.getTime() / 1000, {
                            shouldValidate: true,
                            shouldDirty: true,
                            shouldTouch: true
                          });
                        }

                        const endTimeValue = form.getValues("end_time");
                        if (endTimeValue) {
                          const endDateTime = new Date(endTimeValue * 1000);
                          const newEndDate = new Date(date);
                          newEndDate.setHours(
                            endDateTime.getHours(),
                            endDateTime.getMinutes(),
                            endDateTime.getSeconds()
                          );
                          form.setValue("end_time", newEndDate.getTime() / 1000, {
                            shouldValidate: true,
                            shouldDirty: true,
                            shouldTouch: true
                          });
                        }
                      }
                    }}
                  />
                </div>
              </FormItem>
            )}

            <FormField
              control={form.control}
              name="case"
              render={({ field }) => {
                return (
                  <FormItem className='grid grid-cols-1 sm:grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                    <FormLabel className='sm:col-span-2 text-right'>
                      {t("case")} {meetingType === "office" && <span className="text-gray-500 text-sm">({t("optional")})</span>}
                    </FormLabel>
                    <FormControl className="sm:col-span-4">
                      <PaginatedSelect
                        defaultValue={meetingToEdit?.case}
                        placeholder={t('select_case')}
                        fetchItems={async v => {
                          const res = await fetchCases(v)
                          setCases(res)
                          return res
                        }}
                        onSelect={(v) => {
                          const ass = cases?.items.find(c => c.id == v?.id)?.assigned_to || null
                          setAssigned(ass)
                          field.onChange(v?.id)
                          form.setValue('assigned_to', ass?.id || "", {
                            shouldValidate: true,
                            shouldDirty: true,
                            shouldTouch: true
                          });
                        }}
                        triggerId="appointment-case-trigger"
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage className='sm:col-span-4 sm:col-start-3' />
                  </FormItem>
                )
              }}
            />

            {/* Meeting Type Radio Buttons */}
            <FormField
              control={form.control}
              name="meetingType"
              render={({ field }) => (
                <FormItem className='grid grid-cols-1 sm:grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                  <FormLabel className='sm:col-span-2 text-right'>
                    {t("meetingType")}
                  </FormLabel>
                  <FormControl className="sm:col-span-4">
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex gap-2"
                    >
                      <FormItem className="flex items-center space-x-2 space-x-reverse ">
                        <FormControl>
                          <RadioGroupItem value="court" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer">
                          {t("court")}
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-2 space-x-reverse">
                        <FormControl>
                          <RadioGroupItem value="office" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer">
                          {t("office")}
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage className='sm:col-span-4 sm:col-start-3' />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="assigned_to"
              render={({ field }) => (
                <FormItem className='grid grid-cols-1 sm:grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                  <FormLabel className='sm:col-span-2 text-right'>
                    {t("assigned_to")}
                  </FormLabel>
                  <FormControl className="sm:col-span-4">
                    <PaginatedSelect
                      defaultValue={meetingToEdit?.assigned_to || undefined}
                      value={assigned || undefined}
                      placeholder={t('select_user')}
                      fetchItems={fetchUsers}
                      onSelect={v => {
                        if (v) {
                          setAssigned(v as Person)
                        }
                        field.onChange(v?.id)
                      }}
                      triggerId="appointment-assigned-to-trigger"
                      className="w-full"
                    />
                  </FormControl>
                  <FormMessage className='sm:col-span-4 sm:col-start-3' />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="start_time"
              render={({ field }) => {
                const start = toHM(field.value);
                return <FormItem className='grid grid-cols-1 sm:grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                  <FormLabel className='sm:col-span-2 text-right'>
                    {t("startTime")}
                  </FormLabel>
                  <TimePicker
                    className="sm:col-span-4"
                    default_hour={start?.hour}
                    default_minute={start?.minute}
                    onTimeChange={v => {
                      const newStart = toTimestamp(v);
                      const prevStart = form.getValues("start_time");
                      const prevEnd = form.getValues("end_time");
                      field.onChange(newStart);
                      // End follows the start, keeping the previous duration (1h by default).
                      // Only an end typed before any start, and still after it, is left alone.
                      const keepEnd = !prevStart && prevEnd && prevEnd > newStart;
                      if (!keepEnd) {
                        const duration = prevStart && prevEnd && prevEnd > prevStart ? prevEnd - prevStart : 3600;
                        form.setValue("end_time", newStart + duration, {
                          shouldValidate: form.formState.isSubmitted,
                          shouldDirty: true,
                        });
                      }
                    }}
                    triggerId="appointment-start-time"
                  />
                  <FormMessage className='sm:col-span-4 sm:col-start-3' />
                </FormItem>
              }}
            />

            <FormField
              control={form.control}
              name="end_time"
              render={({ field }) => {
                const end = toHM(field.value);
                return <FormItem className='grid grid-cols-1 sm:grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                  <FormLabel className='sm:col-span-2 text-right'>
                    {t("endTime")}
                  </FormLabel>
                  <TimePicker
                    className="sm:col-span-4"
                    default_hour={end?.hour}
                    default_minute={end?.minute}
                    minHour={startHM?.hour}
                    minMinute={startHM?.minute}
                    referenceTime={startHM}
                    onTimeChange={v => field.onChange(toTimestamp(v))}
                    triggerId="appointment-end-time"
                  />
                  <FormMessage className='sm:col-span-4 sm:col-start-3' />
                </FormItem>
              }}
            />

            <FormField
              control={form.control}
              name="desc"
              render={({ field }) => (
                <FormItem className='grid grid-cols-1 sm:grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                  <FormLabel className='sm:col-span-2 text-right'>
                    {t("description")}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t("desc_desc")}
                      className="resize-none sm:col-span-4"
                      {...field}
                      value={field?.value || ""}
                    />
                  </FormControl>
                  <FormMessage className='sm:col-span-4 sm:col-start-3' />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 pt-4">
              <Button type="submit">
                {postponeMode ? t("postpone") : t("save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
